/**
 * <AiStatusButton /> — top-bar AI Status control (M21.10).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pause, Play, Sparkles } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { api } from "../lib/api";
import type { AiStatus } from "../lib/api";

const _MODE_LABEL_KEYS: Record<AiStatus["autonomy_mode"], string> = {
  conservative: "app.ai_status_mode_conservative",
  standard: "app.ai_status_mode_standard",
  aggressive: "app.ai_status_mode_aggressive",
};

export function AiStatusButton() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const q = useQuery({
    queryKey: ["ai-status"],
    queryFn: () => api.getAiStatus(),
    refetchInterval: 60_000,
  });

  const pauseM = useMutation({
    mutationFn: () => (q.data?.paused ? api.resumeAi() : api.pauseAi()),
    onSuccess: (next) => {
      qc.setQueryData(["ai-status"], next);
    },
  });

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const status = q.data;

  const chipLabel = status
    ? status.paused
      ? t("app.ai_status_paused_short")
      : t(_MODE_LABEL_KEYS[status.autonomy_mode])
    : t("app.ai_status_short");

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t("app.ai_status_aria", { label: chipLabel })}
        className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-medium transition-colors ${
          status?.paused
            ? "border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100"
            : "border-ai-subtle bg-ai-bg text-ai hover:brightness-95"
        }`}
      >
        <Sparkles
          size={12}
          aria-hidden
          className={status?.paused ? "" : "text-brand-teal"}
        />
        <span>{chipLabel}</span>
      </button>

      {open && status && (
        <div
          role="dialog"
          aria-label={t("app.ai_status_dialog_aria")}
          style={{ backgroundColor: "var(--surface-default, #ffffff)" }}
          className="absolute end-0 z-50 mt-2 w-72 rounded-xl border border-border-subtle p-4 shadow-lg"
        >
          <header className="flex items-center gap-2">
            <Sparkles size={14} className="text-brand" />
            <h3 className="text-sm font-semibold text-text-primary">
              {t("app.ai_status_agents")}
            </h3>
            <span
              className={`ms-auto rounded-full px-2 py-0.5 text-[10px] font-medium ${
                status.paused
                  ? "bg-amber-100 text-amber-700"
                  : "bg-emerald-100 text-emerald-700"
              }`}
            >
              {status.paused
                ? t("app.ai_status_paused_short")
                : t("app.ai_status_healthy")}
            </span>
          </header>

          <dl className="mt-3 grid grid-cols-2 gap-3 text-xs">
            <div>
              <dt className="text-text-secondary">{t("app.ai_status_autonomy_mode")}</dt>
              <dd className="mt-0.5 font-semibold text-text-primary">
                {t(_MODE_LABEL_KEYS[status.autonomy_mode])}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t("app.ai_status_actions_today")}</dt>
              <dd className="mt-0.5 font-semibold tabular-nums text-text-primary">
                {status.actions_today}
              </dd>
            </div>
            <div>
              <dt className="text-text-secondary">{t("app.ai_status_escalations_today")}</dt>
              <dd className="mt-0.5 font-semibold tabular-nums text-text-primary">
                {status.escalations_today}
              </dd>
            </div>
          </dl>

          <button
            type="button"
            onClick={() => pauseM.mutate()}
            disabled={pauseM.isPending}
            className={`mt-4 inline-flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-50 ${
              status.paused
                ? "bg-emerald-700 text-white hover:bg-emerald-700"
                : "bg-amber-600 text-white hover:bg-amber-700"
            }`}
          >
            {status.paused ? (
              <>
                <Play size={14} /> {t("app.ai_status_resume_all")}
              </>
            ) : (
              <>
                <Pause size={14} /> {t("app.ai_status_pause_all")}
              </>
            )}
          </button>

          <div className="mt-3 flex flex-col gap-1 border-t border-border-subtle pt-3 text-xs">
            <Link
              to="/ai/activity-log"
              onClick={() => setOpen(false)}
              className="text-brand hover:underline"
            >
              {t("app.ai_status_view_activity")}
            </Link>
            <Link
              to="/ai/behavior"
              onClick={() => setOpen(false)}
              className="text-brand hover:underline"
            >
              {t("app.ai_status_change_mode")}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
